"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  HelpCircle, X, Send, Loader2, Navigation, BookOpen, GraduationCap, Search,
  HelpCircle as QuestionIcon, Lightbulb, Users, PhoneOff, Shield, Eye,
  MousePointer2, CheckCircle, AlertCircle, MessageCircle, Play, RotateCcw,
  Home, Phone, Calendar, Bell, FileText, Folder, Trophy, Settings,
  Mail, Video, Headphones, UserPlus, Building2, ChevronRight,
  ClipboardCheck, Gift, MapPin, MessageSquare
} from "lucide-react";
import Image from "next/image";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { useTour } from "../onboarding-tour";
import { tourRegistry, getAllTours, INTERACTIVE_TOURS_ENABLED } from "../../config/tours";
import insuraHelpService, { ChatMessage as ServiceChatMessage } from "../../services/insuraHelpService";
import { useCoBrowse, CoBrowseSession } from "../cobrowse/CoBrowseProvider";
import { useUnifiedDialer } from "../../contexts/UnifiedDialerContext";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";

// Quick action examples for users to click
interface QuickAction {
  icon: React.ReactNode;
  label: string;
  prompt: string;
  category: "navigate" | "explain" | "tour" | "find" | "answer" | "suggest" | "livehelp" | "quick";
  // Optional role-based visibility
  agencyOwnerOnly?: boolean;
  adminOnly?: boolean;
  // Optional feature-flag gate — hidden unless that flag is enabled for the
  // user's agency (e.g. Quote & Enroll → `quoteAndEnroll`, MRFG + downline only).
  featureFlag?: string;
}

const quickActions: QuickAction[] = [
  // Navigation - Expanded
  { icon: <Home className="w-3 h-3" />, label: "Dashboard", prompt: "[NAVIGATE:/dashboard]", category: "navigate" },
  { icon: <Users className="w-3 h-3" />, label: "Leads", prompt: "[NAVIGATE:/leads]", category: "navigate" },
  { icon: <Phone className="w-3 h-3" />, label: "Dialer", prompt: "[OPEN_DIALER_WIDGET]", category: "navigate" },
  { icon: <MessageCircle className="w-3 h-3" />, label: "Communication", prompt: "[NAVIGATE:/communication]", category: "navigate" },
  { icon: <FileText className="w-3 h-3" />, label: "Fax", prompt: "[NAVIGATE:/fax]", category: "navigate" },
  { icon: <Calendar className="w-3 h-3" />, label: "Appointments", prompt: "[NAVIGATE:/appointments]", category: "navigate" },
  { icon: <Bell className="w-3 h-3" />, label: "Reminders", prompt: "[NAVIGATE:/reminders]", category: "navigate" },
  { icon: <Folder className="w-3 h-3" />, label: "Resources", prompt: "[NAVIGATE:/resources]", category: "navigate" },
  { icon: <Trophy className="w-3 h-3" />, label: "Leaderboard", prompt: "[NAVIGATE:/leaderboard]", category: "navigate" },
  { icon: <Building2 className="w-3 h-3" />, label: "Agency Dashboard", prompt: "[NAVIGATE:/agency-dashboard]", category: "navigate", agencyOwnerOnly: true },
  { icon: <UserPlus className="w-3 h-3" />, label: "Lead Assignment", prompt: "[NAVIGATE:/lead-assignment]", category: "navigate", agencyOwnerOnly: true },
  { icon: <MessageSquare className="w-3 h-3" />, label: "Team Chat", prompt: "[NAVIGATE:/team-chat]", category: "navigate" },
  { icon: <ClipboardCheck className="w-3 h-3" />, label: "Quote & Enroll", prompt: "[NAVIGATE:/quote-enroll]", category: "navigate", featureFlag: "quoteAndEnroll" },
  { icon: <MapPin className="w-3 h-3" />, label: "Routes", prompt: "[NAVIGATE:/routes]", category: "navigate" },
  { icon: <Gift className="w-3 h-3" />, label: "Rewards", prompt: "[NAVIGATE:/agent-rewards]", category: "navigate" },
  { icon: <Settings className="w-3 h-3" />, label: "Settings", prompt: "[NAVIGATE:/settings]", category: "navigate" },

  // Live Help - triggers co-browse
  { icon: <Headphones className="w-3 h-3" />, label: "Get Live Help", prompt: "[REQUEST_LIVE_HELP]", category: "livehelp" },
  { icon: <Mail className="w-3 h-3" />, label: "Contact Support", prompt: "[SHOW_SUPPORT_EMAIL]", category: "livehelp" },

  // Quick Actions - These are AI prompts for the helper bot
  { icon: <QuestionIcon className="w-3 h-3" />, label: "Tips for this page", prompt: "Give me tips for using this page effectively", category: "quick" },
  { icon: <Search className="w-3 h-3" />, label: "Find a setting", prompt: "Help me find a specific setting", category: "quick" },
  { icon: <BookOpen className="w-3 h-3" />, label: "How does this work?", prompt: "Explain how this page works and what I can do here", category: "quick" },
  { icon: <Lightbulb className="w-3 h-3" />, label: "Keyboard shortcuts", prompt: "What keyboard shortcuts are available on this page?", category: "quick" },

  // Explain - Dashboard
  { icon: <Home className="w-3 h-3" />, label: "Dashboard overview", prompt: "Explain the dashboard and what each section shows", category: "explain" },
  { icon: <Home className="w-3 h-3" />, label: "Dashboard widgets", prompt: "What widgets are available on the dashboard?", category: "explain" },
  { icon: <Home className="w-3 h-3" />, label: "Dashboard metrics", prompt: "What metrics are tracked on the dashboard?", category: "explain" },

  // Explain - Leads
  { icon: <Users className="w-3 h-3" />, label: "Import leads", prompt: "How do I import leads from a CSV file?", category: "explain" },
  { icon: <Users className="w-3 h-3" />, label: "Lead statuses", prompt: "What do the different lead statuses mean?", category: "explain" },
  { icon: <Users className="w-3 h-3" />, label: "Lead filters", prompt: "How do I filter and search leads?", category: "explain" },
  { icon: <Users className="w-3 h-3" />, label: "Lead assignment", prompt: "How do I assign leads to agents?", category: "explain" },
  { icon: <Users className="w-3 h-3" />, label: "Lead sources", prompt: "What are the different lead sources?", category: "explain" },

  // Explain - Dialer
  { icon: <Phone className="w-3 h-3" />, label: "Power dialer", prompt: "How do I use the power dialer?", category: "explain" },
  { icon: <Phone className="w-3 h-3" />, label: "Multi-line dial", prompt: "What is multi-line dialing and how does it work?", category: "explain" },
  { icon: <Phone className="w-3 h-3" />, label: "Call recordings", prompt: "How do I access call recordings?", category: "explain" },
  { icon: <Phone className="w-3 h-3" />, label: "Voicemail drops", prompt: "How do I use voicemail drops?", category: "explain" },
  { icon: <Phone className="w-3 h-3" />, label: "Call dispositions", prompt: "What are call dispositions and how do I use them?", category: "explain" },

  // Explain - Communication
  { icon: <MessageCircle className="w-3 h-3" />, label: "Send SMS", prompt: "How do I send text messages to leads?", category: "explain" },
  { icon: <MessageCircle className="w-3 h-3" />, label: "Send email", prompt: "How do I send emails to leads?", category: "explain" },
  { icon: <MessageCircle className="w-3 h-3" />, label: "Email templates", prompt: "How do I create and use email templates?", category: "explain" },
  { icon: <MessageCircle className="w-3 h-3" />, label: "SMS templates", prompt: "How do I create and use SMS templates?", category: "explain" },
  { icon: <MessageCircle className="w-3 h-3" />, label: "Bulk messaging", prompt: "How do I send bulk messages?", category: "explain" },

  // Explain - Fax
  { icon: <FileText className="w-3 h-3" />, label: "Send fax", prompt: "How do I send a fax?", category: "explain" },
  { icon: <FileText className="w-3 h-3" />, label: "Fax history", prompt: "Where can I view my fax history?", category: "explain" },

  // Explain - Appointments
  { icon: <Calendar className="w-3 h-3" />, label: "Schedule appointment", prompt: "How do I schedule an appointment?", category: "explain" },
  { icon: <Calendar className="w-3 h-3" />, label: "Calendar sync", prompt: "How do I sync with my calendar?", category: "explain" },
  { icon: <Calendar className="w-3 h-3" />, label: "Appointment reminders", prompt: "How do appointment reminders work?", category: "explain" },

  // Explain - Reminders
  { icon: <Bell className="w-3 h-3" />, label: "Set reminder", prompt: "How do I set a reminder?", category: "explain" },
  { icon: <Bell className="w-3 h-3" />, label: "Recurring reminders", prompt: "Can I create recurring reminders?", category: "explain" },

  // Explain - Resources
  { icon: <Folder className="w-3 h-3" />, label: "Upload resources", prompt: "How do I upload training resources?", category: "explain" },
  { icon: <Folder className="w-3 h-3" />, label: "Share resources", prompt: "How do I share resources with my team?", category: "explain" },

  // Explain - Leaderboard
  { icon: <Trophy className="w-3 h-3" />, label: "Leaderboard metrics", prompt: "What metrics are tracked on the leaderboard?", category: "explain" },
  { icon: <Trophy className="w-3 h-3" />, label: "Leaderboard periods", prompt: "What time periods can I view on the leaderboard?", category: "explain" },

  // Explain - Agency Dashboard
  { icon: <Building2 className="w-3 h-3" />, label: "Agency overview", prompt: "Explain the agency dashboard and what it shows", category: "explain" },
  { icon: <Building2 className="w-3 h-3" />, label: "Sub-agencies", prompt: "How do sub-agencies and the agency hierarchy work?", category: "explain" },
  { icon: <Building2 className="w-3 h-3" />, label: "Agency trends", prompt: "What agency-wide stats and trends are available and how do I read them?", category: "explain" },
  { icon: <Building2 className="w-3 h-3" />, label: "Policy map", prompt: "How do I read the policy map and premium sold by state?", category: "explain" },

  // Explain - Team Chat
  { icon: <MessageSquare className="w-3 h-3" />, label: "Using team chat", prompt: "How does team chat work?", category: "explain" },
  { icon: <MessageSquare className="w-3 h-3" />, label: "Chat channels", prompt: "How do I create and use team chat channels?", category: "explain" },

  // Explain - Quote & Enroll (MRFG-flagged)
  { icon: <ClipboardCheck className="w-3 h-3" />, label: "Run a quote", prompt: "How do I run a quote and start an application in Quote & Enroll?", category: "explain", featureFlag: "quoteAndEnroll" },
  { icon: <ClipboardCheck className="w-3 h-3" />, label: "e-Application", prompt: "How does the e-application (eApp) enrollment process work?", category: "explain", featureFlag: "quoteAndEnroll" },

  // Explain - Lead Assignment
  { icon: <UserPlus className="w-3 h-3" />, label: "Assign leads", prompt: "How does lead assignment to agents work?", category: "explain" },
  { icon: <UserPlus className="w-3 h-3" />, label: "Bulk assign", prompt: "How do I bulk-assign or reassign leads to my team?", category: "explain" },

  // Explain - Routes
  { icon: <MapPin className="w-3 h-3" />, label: "Plan a route", prompt: "How do I plan a door-knocking route to my leads?", category: "explain" },
  { icon: <MapPin className="w-3 h-3" />, label: "Optimize route", prompt: "How does route optimization and GPS navigation work?", category: "explain" },

  // Explain - Rewards
  { icon: <Gift className="w-3 h-3" />, label: "Agent rewards", prompt: "How do agent referral rewards work?", category: "explain" },
  { icon: <Gift className="w-3 h-3" />, label: "Redeem rewards", prompt: "How do I earn and redeem rewards?", category: "explain" },


  // Find - Settings
  { icon: <Settings className="w-3 h-3" />, label: "Change password", prompt: "Where can I change my password?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Enable 2FA", prompt: "Where do I enable two-factor authentication?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Notification settings", prompt: "Where can I manage notification settings?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Profile settings", prompt: "Where do I update my profile information?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Billing settings", prompt: "Where can I find billing and subscription settings?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Phone numbers", prompt: "Where do I add or manage phone numbers?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Email integration", prompt: "Where do I connect my email?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Calendar integration", prompt: "Where do I connect my calendar?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Team settings", prompt: "Where do I manage team members?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "Agency settings", prompt: "Where do I find agency settings?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "10DLC registration", prompt: "Where do I register for 10DLC?", category: "find" },
  { icon: <Settings className="w-3 h-3" />, label: "API keys", prompt: "Where do I find my API keys?", category: "find" },

  // Answer - General
  { icon: <QuestionIcon className="w-3 h-3" />, label: "What is 10DLC?", prompt: "What does 10DLC mean and why do I need it?", category: "answer" },
  { icon: <QuestionIcon className="w-3 h-3" />, label: "Power vs Multi-line", prompt: "What's the difference between power dial and multi-line dial?", category: "answer" },
  { icon: <QuestionIcon className="w-3 h-3" />, label: "Lead scoring", prompt: "How does lead scoring work?", category: "answer" },
  { icon: <QuestionIcon className="w-3 h-3" />, label: "Call recording laws", prompt: "What are the call recording consent laws?", category: "answer" },
  { icon: <QuestionIcon className="w-3 h-3" />, label: "Best calling times", prompt: "What are the best times to call leads?", category: "answer" },

  // Suggest
  { icon: <Lightbulb className="w-3 h-3" />, label: "Best practices", prompt: "What are the best practices for managing leads?", category: "suggest" },
  { icon: <Lightbulb className="w-3 h-3" />, label: "Improve conversions", prompt: "How can I improve my lead conversion rate?", category: "suggest" },
  { icon: <Lightbulb className="w-3 h-3" />, label: "Call scripts", prompt: "Can you give me tips for effective call scripts?", category: "suggest" },
  { icon: <Lightbulb className="w-3 h-3" />, label: "Time management", prompt: "Tips for managing my time as an agent", category: "suggest" },
  { icon: <Lightbulb className="w-3 h-3" />, label: "Follow-up strategy", prompt: "What's a good follow-up strategy for leads?", category: "suggest" },
];

const categoryColors: Record<QuickAction["category"], string> = {
  navigate: "bg-primary/5 text-primary hover:bg-primary/10 border-primary/20",
  explain: "bg-green-50 text-green-700 hover:bg-green-100 border-green-200",
  tour: "bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200",
  find: "bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-200",
  answer: "bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border-cyan-200",
  suggest: "bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200",
  livehelp: "bg-red-50 text-red-700 hover:bg-red-100 border-red-200",
  quick: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200",
};

// InsuraBot logo icon - uses branding logo or falls back to favicon
const InsuraBotIcon = ({ size = 20, altText = "InsuraBot", logoSrc }: { size?: number; altText?: string; logoSrc?: string }) => (
  <Image
    src={logoSrc || "/favicon.png"}
    alt={altText}
    width={size}
    height={size}
    className="object-contain"
  />
);

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  isStreaming?: boolean; // Flag for streaming messages
}

/**
 * Navigate Grid Component
 * Shows 6 items by default with a "Show more" button to reveal the rest
 */
function NavigateGrid({ actions, onAction }: { actions: QuickAction[]; onAction: (prompt: string) => void }) {
  const [showAll, setShowAll] = useState(false);
  const displayedActions = showAll ? actions : actions.slice(0, 6);
  const hasMore = actions.length > 6;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-1">
        {displayedActions.map((action, index) => (
          <button
            key={`nav-${index}`}
            onClick={() => onAction(action.prompt)}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-primary/5 transition-colors text-gray-600 hover:text-primary"
          >
            <span className="p-1.5 bg-primary/10 rounded-lg text-primary">
              {action.icon}
            </span>
            <span className="text-[10px] font-medium text-center leading-tight">{action.label}</span>
          </button>
        ))}
      </div>
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full text-[11px] text-primary hover:text-primary/80 py-1 flex items-center justify-center gap-1"
        >
          {showAll ? (
            <>Show less</>
          ) : (
            <>Show {actions.length - 6} more</>
          )}
          <ChevronRight className={`w-3 h-3 transition-transform ${showAll ? 'rotate-90' : ''}`} />
        </button>
      )}
    </div>
  );
}

/**
 * Help Topics Content Component
 * Shows help topics with "Show more" functionality
 */
function HelpTopicsContent({
  explainActions,
  findActions,
  answerActions,
  suggestActions,
  relevantCount,
  categoryColors,
  onAction,
}: {
  explainActions: QuickAction[];
  findActions: QuickAction[];
  answerActions: QuickAction[];
  suggestActions: QuickAction[];
  relevantCount: number;
  categoryColors: Record<QuickAction["category"], string>;
  onAction: (prompt: string) => void;
}) {
  const [showMoreExplain, setShowMoreExplain] = useState(false);
  const [showMoreFind, setShowMoreFind] = useState(false);
  const [showMoreLearn, setShowMoreLearn] = useState(false);

  const displayedExplain = showMoreExplain ? explainActions : explainActions.slice(0, Math.max(4, relevantCount));
  const displayedFind = showMoreFind ? findActions : findActions.slice(0, 4);
  const learnActions = [...answerActions, ...suggestActions];
  const displayedLearn = showMoreLearn ? learnActions : learnActions.slice(0, 5);

  return (
    <div className="space-y-2">
      {/* Explain - How to... */}
      <div>
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          How to...
          {relevantCount > 0 && <span className="ml-1 text-green-600">({relevantCount} for this page)</span>}
        </p>
        <div className="flex flex-wrap gap-1">
          {displayedExplain.map((action, index) => (
            <button
              key={`explain-${index}`}
              onClick={() => onAction(action.prompt)}
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-full border transition-colors ${categoryColors.explain}`}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
        {explainActions.length > Math.max(4, relevantCount) && (
          <button
            onClick={() => setShowMoreExplain(!showMoreExplain)}
            className="text-[10px] text-primary hover:text-primary/80 mt-1"
          >
            {showMoreExplain ? 'Show less' : `Show ${explainActions.length - Math.max(4, relevantCount)} more`}
          </button>
        )}
      </div>

      {/* Find - Where is... */}
      <div>
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Where is...</p>
        <div className="flex flex-wrap gap-1">
          {displayedFind.map((action, index) => (
            <button
              key={`find-${index}`}
              onClick={() => onAction(action.prompt)}
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-full border transition-colors ${categoryColors.find}`}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
        {findActions.length > 4 && (
          <button
            onClick={() => setShowMoreFind(!showMoreFind)}
            className="text-[10px] text-primary hover:text-primary/80 mt-1"
          >
            {showMoreFind ? 'Show less' : `Show ${findActions.length - 4} more`}
          </button>
        )}
      </div>

      {/* Learn about... */}
      <div>
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Learn about...</p>
        <div className="flex flex-wrap gap-1">
          {displayedLearn.map((action, index) => (
            <button
              key={`learn-${index}`}
              onClick={() => onAction(action.prompt)}
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-full border transition-colors ${categoryColors[action.category]}`}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
        {learnActions.length > 5 && (
          <button
            onClick={() => setShowMoreLearn(!showMoreLearn)}
            className="text-[10px] text-primary hover:text-primary/80 mt-1"
          >
            {showMoreLearn ? 'Show less' : `Show ${learnActions.length - 5} more`}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Quick Actions Panel Component
 * Collapsible panel with preset action buttons organized into sections
 * Includes Tours, Navigation, Live Help, and Quick Actions
 */
interface QuickActionsPanelProps {
  quickActions: QuickAction[];
  categoryColors: Record<QuickAction["category"], string>;
  onAction: (prompt: string) => void;
  onStartTour: (tourId: string) => void;
  onResetAllTours: () => void;
  isInitialState: boolean;
  availableTours: Array<{ id: string; name: string; page: string }>;
  tourProgress: Record<string, { status: string }>;
  currentPath: string | null;
  isAgencyOwner: boolean;
  isAdmin: boolean;
  enabledFeatures?: Record<string, boolean>;
  forceCollapse?: boolean;
}

function QuickActionsPanel({
  quickActions,
  categoryColors,
  onAction,
  onStartTour,
  onResetAllTours,
  isInitialState,
  availableTours,
  tourProgress,
  currentPath,
  isAgencyOwner,
  isAdmin,
  enabledFeatures = {},
  forceCollapse,
}: QuickActionsPanelProps) {
  // Filter actions based on user role + feature flags
  const filteredQuickActions = quickActions.filter(action => {
    if (action.adminOnly && !isAdmin) return false;
    if (action.agencyOwnerOnly && !isAgencyOwner) return false;
    // Feature-gated actions (e.g. Quote & Enroll) hidden unless the flag is on.
    if (action.featureFlag && !enabledFeatures[action.featureFlag]) return false;
    return true;
  });
  // Main panel expanded state
  const [isPanelExpanded, setIsPanelExpanded] = useState(isInitialState);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    tours: isInitialState,
    navigate: false,
    livehelp: false,
    quick: isInitialState,
    help: false,
  });

  // Auto-expand in initial state
  useEffect(() => {
    if (isInitialState) {
      setIsPanelExpanded(true);
      setExpandedSections(prev => ({ ...prev, tours: true, quick: true }));
    }
  }, [isInitialState]);

  // Collapse when forceCollapse is true
  useEffect(() => {
    if (forceCollapse) {
      setIsPanelExpanded(false);
    }
  }, [forceCollapse]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Group actions by category (using filtered actions)
  const navigateActions = filteredQuickActions.filter(a => a.category === "navigate");
  const liveHelpActions = filteredQuickActions.filter(a => a.category === "livehelp");
  const quickActionItems = filteredQuickActions.filter(a => a.category === "quick");
  const allExplainActions = filteredQuickActions.filter(a => a.category === "explain");
  const findActions = filteredQuickActions.filter(a => a.category === "find");
  const answerActions = filteredQuickActions.filter(a => a.category === "answer");
  const suggestActions = filteredQuickActions.filter(a => a.category === "suggest");

  // Get page-specific help topics based on current path
  const getPageIcon = (): React.ReactNode => {
    if (!currentPath) return null;
    // Agency dashboard & lead-assignment checked before the broader ones so
    // they don't get shadowed.
    if (currentPath.includes('/agency-dashboard')) return <Building2 className="w-3 h-3" />;
    if (currentPath.includes('/lead-assignment')) return <UserPlus className="w-3 h-3" />;
    if (currentPath.includes('/dashboard')) return <Home className="w-3 h-3" />;
    if (currentPath.includes('/leads')) return <Users className="w-3 h-3" />;
    if (currentPath.includes('/dialer')) return <Phone className="w-3 h-3" />;
    if (currentPath.includes('/communication')) return <MessageCircle className="w-3 h-3" />;
    if (currentPath.includes('/team-chat')) return <MessageSquare className="w-3 h-3" />;
    if (currentPath.includes('/quote-enroll')) return <ClipboardCheck className="w-3 h-3" />;
    if (currentPath.includes('/fax')) return <FileText className="w-3 h-3" />;
    if (currentPath.includes('/appointments')) return <Calendar className="w-3 h-3" />;
    if (currentPath.includes('/reminders')) return <Bell className="w-3 h-3" />;
    if (currentPath.includes('/resources')) return <Folder className="w-3 h-3" />;
    if (currentPath.includes('/routes')) return <MapPin className="w-3 h-3" />;
    if (currentPath.includes('/rewards')) return <Gift className="w-3 h-3" />;
    if (currentPath.includes('/leaderboard')) return <Trophy className="w-3 h-3" />;
    if (currentPath.includes('/settings')) return <Settings className="w-3 h-3" />;
    return null;
  };

  // Filter explain actions to show page-relevant ones first
  const pageIcon = getPageIcon();
  const relevantExplainActions = allExplainActions.filter(a => {
    if (!currentPath) return false;
    const p = a.prompt.toLowerCase();
    // Match actions whose topic matches the current page (most specific first).
    if (currentPath.includes('/agency-dashboard') && (p.includes('agency') || p.includes('sub-agenc') || p.includes('policy map'))) return true;
    if (currentPath.includes('/lead-assignment') && (p.includes('assign') || p.includes('reassign'))) return true;
    if (currentPath.includes('/dashboard') && p.includes('dashboard')) return true;
    if (currentPath.includes('/leads') && (p.includes('lead') || p.includes('import'))) return true;
    if (currentPath.includes('/dialer') && (p.includes('dial') || p.includes('call') || p.includes('voicemail'))) return true;
    if (currentPath.includes('/team-chat') && p.includes('chat')) return true;
    if (currentPath.includes('/communication') && (p.includes('sms') || p.includes('email') || p.includes('message'))) return true;
    if (currentPath.includes('/quote-enroll') && (p.includes('quote') || p.includes('application') || p.includes('eapp') || p.includes('enroll'))) return true;
    if (currentPath.includes('/fax') && p.includes('fax')) return true;
    if (currentPath.includes('/appointments') && (p.includes('appointment') || p.includes('calendar'))) return true;
    if (currentPath.includes('/reminders') && p.includes('reminder')) return true;
    if (currentPath.includes('/resources') && p.includes('resource')) return true;
    if (currentPath.includes('/routes') && p.includes('route')) return true;
    if (currentPath.includes('/rewards') && p.includes('reward')) return true;
    if (currentPath.includes('/leaderboard') && p.includes('leaderboard')) return true;
    return false;
  });

  // Get other explain actions (not relevant to current page)
  const otherExplainActions = allExplainActions.filter(a => !relevantExplainActions.includes(a));

  // Combine: relevant first, then others
  const explainActions = [...relevantExplainActions, ...otherExplainActions];

  // Get tour status badge
  const getTourStatus = (tourId: string) => {
    const progress = tourProgress[tourId];
    if (!progress) return null;
    if (progress.status === 'completed') return { label: 'Done', color: 'bg-green-100 text-green-700' };
    if (progress.status === 'skipped') return { label: 'Skipped', color: 'bg-gray-100 text-gray-600' };
    if (progress.status === 'in_progress') return { label: 'In Progress', color: 'bg-blue-100 text-blue-700' };
    return null;
  };

  // Section component for consistent styling
  const Section = ({
    id,
    title,
    icon: Icon,
    children,
    count,
  }: {
    id: string;
    title: string;
    icon: React.ElementType;
    children: React.ReactNode;
    count?: number;
  }) => {
    const sectionRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
      const wasExpanded = expandedSections[id];
      toggleSection(id);
      // If we're expanding (not collapsing), scroll to section after animation
      if (!wasExpanded) {
        setTimeout(() => {
          sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 200);
      }
    };

    return (
      <div ref={sectionRef} className="border-b border-gray-100 last:border-b-0">
        <button
          onClick={handleToggle}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">{title}</span>
            {count !== undefined && (
              <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                {count}
              </span>
            )}
          </div>
          <motion.span
            animate={{ rotate: expandedSections[id] ? 180 : 0 }}
            transition={{ duration: 0.15 }}
            className="text-gray-400"
          >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {expandedSections[id] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="px-3 pb-2">
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden"
    >
      {/* Main Quick Actions Header - Click to expand/collapse all */}
      <button
        onClick={() => setIsPanelExpanded(!isPanelExpanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-primary/5 hover:bg-primary/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-gray-800">Quick Actions</span>
        </div>
        <motion.span
          animate={{ rotate: isPanelExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-gray-500"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.span>
      </button>

      {/* Expandable Content */}
      <AnimatePresence initial={false}>
        {isPanelExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {/* Tours Section (hidden while interactive tours are off) */}
            {INTERACTIVE_TOURS_ENABLED && (
            <Section id="tours" title="Tours & Tutorials" icon={GraduationCap} count={availableTours.length}>
              <div className="space-y-2">
                {/* Start Full Tour Button */}
                <button
                  onClick={onResetAllTours}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs font-medium"
                >
                  <Play className="w-3.5 h-3.5" />
                  <span>Start Full Platform Tour</span>
                  <RotateCcw className="w-3 h-3 ml-auto opacity-60" />
                </button>

                {/* Individual Page Tours */}
                <div className="flex flex-wrap gap-1">
                  {availableTours.slice(0, 8).map((tour) => {
                    const status = getTourStatus(tour.id);
                    return (
                      <button
                        key={tour.id}
                        onClick={() => onStartTour(tour.id)}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-full border transition-colors ${categoryColors.tour}`}
                      >
                        <GraduationCap className="w-3 h-3" />
                        <span>{tour.name.replace(' Tour', '')}</span>
                        {status && (
                          <span className={`text-[9px] px-1 py-0 rounded ${status.color}`}>
                            {status.label}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Section>
            )}

            {/* Navigate Section - Show 6 initially, expand for more */}
            <Section id="navigate" title="Navigate" icon={Navigation} count={navigateActions.length}>
              <NavigateGrid actions={navigateActions} onAction={onAction} />
            </Section>

            {/* Live Help Section */}
            <Section id="livehelp" title="Live Help & Support" icon={Headphones} count={liveHelpActions.length}>
              <div className="flex flex-wrap gap-1">
                {liveHelpActions.map((action, index) => (
                  <button
                    key={`help-${index}`}
                    onClick={() => onAction(action.prompt)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${categoryColors.livehelp}`}
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </Section>

            {/* Quick Prompts Section */}
            <Section id="quick" title="Quick Prompts" icon={Lightbulb} count={quickActionItems.length}>
              <div className="flex flex-wrap gap-1">
                {quickActionItems.map((action, index) => (
                  <button
                    key={`quick-${index}`}
                    onClick={() => onAction(action.prompt)}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors ${categoryColors.quick}`}
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </Section>

            {/* Help Topics Section */}
            <Section id="help" title="Help Topics" icon={QuestionIcon} count={explainActions.length + findActions.length + answerActions.length + suggestActions.length}>
              <HelpTopicsContent
                explainActions={explainActions}
                findActions={findActions}
                answerActions={answerActions}
                suggestActions={suggestActions}
                relevantCount={relevantExplainActions.length}
                categoryColors={categoryColors}
                onAction={onAction}
              />
            </Section>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

interface InsuraHelpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  /**
   * When true, the panel fills its parent container instead of floating as an
   * absolutely-positioned card, and its own header is hidden (the host shell —
   * e.g. the InsuraBot "Helper" tab — provides the header). Used to embed the
   * full knowledge-base chat inside UnifiedInsuraBot.
   */
  embedded?: boolean;
}

// InsuraBot robot glyph — the same SVG the Client tab shows next to bot
// messages, so both chats read the same. `pulse` animates while streaming.
const BotGlyph = ({ pulse = false }: { pulse?: boolean }) => (
  <span className="flex-shrink-0 w-5 h-5 mt-0.5 text-primary">
    <svg viewBox="0 0 500 410" fill="none" xmlns="http://www.w3.org/2000/svg" className={`w-5 h-5 overflow-visible ${pulse ? 'animate-pulse' : ''}`}>
      <path d="M386.363 22.731H113.636C88.5321 22.731 68.1814 43.0816 68.1814 68.1855V250.004C68.1814 275.108 88.5321 295.458 113.636 295.458H386.363C411.467 295.458 431.818 275.108 431.818 250.004V68.1855C431.818 43.0816 411.467 22.731 386.363 22.731Z" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M22.7263 386.371H477.272" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
      <path className="insurabot-eye" d="M204.546 113.636V159.091" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
      <path className="insurabot-eye insurabot-eye-r" d="M295.454 113.636V159.091" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </span>
);

/**
 * InsuraHelp Panel - Embedded version for dialer widget
 * Full-featured panel with all the same functionality as the standalone InsuraHelpWidget
 */
export function InsuraHelpPanel({ isOpen, onClose, position = 'left', embedded = false }: InsuraHelpPanelProps) {
  const pathname = usePathname();
  // Desktop-only affordances (AI Assist co-browse, tour quick actions) are
  // hidden on the native shell — they drive desktop UI the phone doesn't have.
  const isMobileShell = !!pathname?.startsWith("/m");
  const router = useRouter();
  const { user } = useUserContext();
  const { branding } = useBranding();
  const { startTour, resetTour, resetAllTours, progress, refreshProgress } = useTour();
  const { openWidget, maximizeWidget } = useUnifiedDialer();
  // Quote & Enroll is MRFG-flagged; gate its Helper quick actions the same way
  // the sidebar does so non-flagged users don't see or ask about it.
  const { enabled: quoteEnrollEnabled } = useFeatureFlag("quoteAndEnroll");

  // Get all available tours
  const availableTours = INTERACTIVE_TOURS_ENABLED ? getAllTours() : [];

  // Co-browse integration
  const coBrowse = useCoBrowse();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState(""); // Current streaming text
  const [showCoBrowsePanel, setShowCoBrowsePanel] = useState(false);
  const [coBrowseError, setCoBrowseError] = useState<string | null>(null);
  const [isStartingCoBrowse, setIsStartingCoBrowse] = useState(false);
  const [liveChatInput, setLiveChatInput] = useState("");
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [collapseQuickActions, setCollapseQuickActions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const liveChatEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Handle starting a co-browse session
  const handleStartCoBrowse = useCallback(async () => {
    if (!coBrowse) return;

    setIsStartingCoBrowse(true);
    setCoBrowseError(null);

    try {
      await coBrowse.startSession();
      // Add a message to the chat
      const coBrowseMessage: ChatMessage = {
        id: `cobrowse-started-${Date.now()}`,
        role: "assistant",
        content: "I've connected you with our live support team. A support agent will join shortly to help guide you through your screen. You'll see their cursor and they can highlight elements to help you.\n\n🔒 Your privacy is protected - passwords are automatically hidden and you can end the session anytime.",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, coBrowseMessage]);
    } catch (err) {
      setCoBrowseError(err instanceof Error ? err.message : 'Failed to start live support');
    } finally {
      setIsStartingCoBrowse(false);
    }
  }, [coBrowse]);

  // Handle ending a co-browse session
  const handleEndCoBrowse = useCallback(() => {
    if (!coBrowse) return;

    coBrowse.endSession();
    setShowCoBrowsePanel(false);
    setShowLiveChat(false);
    setLiveChatInput("");

    // Add a message to the chat
    const endMessage: ChatMessage = {
      id: `cobrowse-ended-${Date.now()}`,
      role: "assistant",
      content: "Live support session ended. Is there anything else I can help you with?",
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, endMessage]);
  }, [coBrowse]);

  // Handle sending live chat message
  const handleSendLiveChat = useCallback(() => {
    if (!liveChatInput.trim() || !coBrowse) return;

    coBrowse.sendChatMessage(liveChatInput.trim());
    setLiveChatInput("");
  }, [liveChatInput, coBrowse]);

  // Handle typing in live chat
  const handleLiveChatTyping = useCallback((value: string) => {
    setLiveChatInput(value);
    if (coBrowse) {
      coBrowse.setTyping(value.length > 0);
    }
  }, [coBrowse]);

  // Scroll live chat to bottom
  useEffect(() => {
    if (coBrowse?.chatMessages?.length) {
      liveChatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [coBrowse?.chatMessages]);

  // Auto-open chat panel when admin joins the session
  useEffect(() => {
    if (coBrowse?.session?.status === 'active' && coBrowse?.session?.adminName) {
      setShowLiveChat(true);
      setShowCoBrowsePanel(true);
    }
  }, [coBrowse?.session?.status, coBrowse?.session?.adminName]);

  // Track previous session state to detect when session ends
  const prevSessionRef = useRef<CoBrowseSession | null>(null);

  // Auto-close co-browse panel when session ends (from admin side)
  useEffect(() => {
    const prevSession = prevSessionRef.current;
    const currentSession = coBrowse?.session;

    // Update the ref for next comparison
    prevSessionRef.current = currentSession || null;

    // If we had a session before but now it's null, session ended
    if (prevSession && !currentSession && showCoBrowsePanel) {
      // Close the panels
      setShowCoBrowsePanel(false);
      setShowLiveChat(false);
      setLiveChatInput("");

      // Add a message to notify the user
      const endMessage: ChatMessage = {
        id: `session-ended-${Date.now()}`,
        role: "assistant",
        content: "The live support session has ended. Is there anything else I can help you with?",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, endMessage]);
    }
  }, [coBrowse?.session, showCoBrowsePanel]);

  // Scroll to bottom when new messages are added or streaming content updates
  useEffect(() => {
    // Only scroll to bottom if there's more than just the welcome message
    if (messages.length > 1 || streamingContent) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, streamingContent]);

  // Scroll to top and focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      // Refresh tour progress when opening
      refreshProgress();
      // Scroll to top when opening
      setTimeout(() => {
        messagesContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, refreshProgress]);

  // Build context for AI
  const buildContext = useCallback(() => {
    return {
      currentPath: pathname,
      userType: user?.user?.userType || "agent",
      accountType: user?.user?.accountType || "basic",
      accountStatus: user?.user?.accountStatus,
      hasAgency: !!user?.currentAgency,
      agencyId: user?.currentAgency?.agency?.id,
      agencyRole: user?.currentAgency?.role,
      userName: user?.user?.name,
      isTrialUser: user?.user?.isTrialUser || false,
      isTrialActive: user?.user?.isTrialActive || user?.isTrialActive || false,
    };
  }, [pathname, user]);

  // Internal function to send a message with streaming - supports auto-continuation
  const sendMessageInternal = useCallback(async (
    messageContent: string,
    currentHistory: ServiceChatMessage[],
    isAutoContinue: boolean = false
  ): Promise<{ wasTruncated: boolean; navigateTo?: string }> => {
    let fullContent = "";
    let navigateTo: string | undefined;
    let wasTruncated = false;

    // Use streaming API for real-time typing effect
    for await (const chunk of insuraHelpService.chatStream(
      messageContent,
      buildContext(),
      currentHistory
    )) {
      if (chunk.type === "chunk" && chunk.content) {
        fullContent += chunk.content;
        // Strip navigation commands from streaming display
        const displayContent = fullContent.replace(/\[NAVIGATE:[^\]]+\]/g, '').trim();
        setStreamingContent(displayContent);
      } else if (chunk.type === "navigate" && chunk.path) {
        navigateTo = chunk.path;
      } else if (chunk.type === "truncated") {
        wasTruncated = true;
      } else if (chunk.type === "done") {
        // Streaming complete
        break;
      }
    }

    // Finalize the message - strip any remaining navigation commands from display
    const cleanContent = fullContent.replace(/\[NAVIGATE:[^\]]+\]/g, '').trim();
    const assistantMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: "assistant",
      content: cleanContent || "I'm here to help! What would you like to know?",
      timestamp: new Date(),
    };

    setStreamingContent("");
    setMessages((prev) => [...prev, assistantMessage]);

    return { wasTruncated, navigateTo };
  }, [buildContext]);

  // Keywords that trigger live help
  const liveHelpKeywords = [
    'live help', 'live support', 'live chat', 'live agent',
    'talk to human', 'talk to agent', 'talk to person', 'talk to someone',
    'speak to human', 'speak to agent', 'speak to person', 'speak to someone',
    'real person', 'real human', 'real agent',
    'human support', 'agent support', 'person support',
    'connect me', 'transfer me', 'escalate',
    'need help from a person', 'need human help',
  ];

  // Check if message contains live help request
  const containsLiveHelpRequest = useCallback((message: string): boolean => {
    const lowerMessage = message.toLowerCase();
    return liveHelpKeywords.some(keyword => lowerMessage.includes(keyword));
  }, []);

  // Handle sending a message with streaming response
  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    const messageContent = inputValue.trim();

    // Check if user is requesting live help
    if (containsLiveHelpRequest(messageContent)) {
      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");

      // Auto-trigger the live help panel
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: "I understand you'd like to speak with a live support agent. Let me connect you with our team right away!",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setShowCoBrowsePanel(true);
      return;
    }

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setStreamingContent("");

    try {
      // Build conversation history for context
      let history: ServiceChatMessage[] = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // Add the user message to history
      history = [...history, { role: "user" as const, content: messageContent }];

      const { wasTruncated, navigateTo } = await sendMessageInternal(
        messageContent,
        history,
        false
      );

      // If response was truncated, auto-continue
      if (wasTruncated) {
        // Get updated history including the truncated response
        const updatedMessages = await new Promise<ChatMessage[]>((resolve) => {
          setMessages((prev) => {
            resolve(prev);
            return prev;
          });
        });

        const updatedHistory: ServiceChatMessage[] = updatedMessages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        // Auto-send continuation request
        await sendMessageInternal(
          "Please continue where you left off.",
          updatedHistory,
          true
        );
      }

      // Handle navigation if AI wants to redirect
      if (navigateTo) {
        if (navigateTo.startsWith("tour:")) {
          const tourId = navigateTo.replace("tour:", "");
          startTour(tourId);
        } else {
          router.push(navigateTo);
        }
      }
    } catch (error: unknown) {
      console.error("InsuraHelp error:", error);
      setStreamingContent("");
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content:
          "I'm sorry, I encountered an error. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, buildContext, router, startTour, sendMessageInternal, containsLiveHelpRequest]);

  // Handle key press
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Handle quick action click - sends the prompt with streaming response
  const handleQuickAction = useCallback(async (prompt: string) => {
    if (isLoading) return;

    // Special handling for live help request
    if (prompt === "[REQUEST_LIVE_HELP]") {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: "I need live help from a support agent",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setShowCoBrowsePanel(true);
      return;
    }

    // Special handling for direct navigation commands
    if (prompt.startsWith("[NAVIGATE:")) {
      const path = prompt.replace("[NAVIGATE:", "").replace("]", "");
      router.push(path);
      onClose();
      return;
    }

    // Special handling for opening the dialer widget
    if (prompt === "[OPEN_DIALER_WIDGET]") {
      openWidget();
      maximizeWidget();
      onClose();
      return;
    }

    // Special handling for showing support email
    if (prompt === "[SHOW_SUPPORT_EMAIL]") {
      // Add a user message to show what was clicked
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: "I'd like to contact support",
        timestamp: new Date(),
      };
      const supportMessage: ChatMessage = {
        id: `support-${Date.now()}`,
        role: "assistant",
        content: `You can reach our support team at:\n\n📧 ${branding.supportEmail}\n\n💬 Join our Discord: https://discord.gg/abTmdT6ktK\n\nWe typically respond within 24 hours on business days. For urgent issues, you can also use the 'Get Live Help' option to connect with an agent in real-time.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage, supportMessage]);
      // Collapse the quick actions panel
      setCollapseQuickActions(true);
      // Scroll to the new message after state updates
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: prompt,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setStreamingContent("");

    try {
      let history: ServiceChatMessage[] = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // Add the user message to history
      history = [...history, { role: "user" as const, content: prompt }];

      const { wasTruncated, navigateTo } = await sendMessageInternal(
        prompt,
        history,
        false
      );

      // If response was truncated, auto-continue
      if (wasTruncated) {
        // Get updated history including the truncated response
        const updatedMessages = await new Promise<ChatMessage[]>((resolve) => {
          setMessages((prev) => {
            resolve(prev);
            return prev;
          });
        });

        const updatedHistory: ServiceChatMessage[] = updatedMessages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        // Auto-send continuation request
        await sendMessageInternal(
          "Please continue where you left off.",
          updatedHistory,
          true
        );
      }

      // Handle navigation
      if (navigateTo) {
        if (navigateTo.startsWith("tour:")) {
          const tourId = navigateTo.replace("tour:", "");
          startTour(tourId);
        } else {
          router.push(navigateTo);
        }
      }
    } catch (error: unknown) {
      console.error("InsuraHelp error:", error);
      setStreamingContent("");
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "I'm sorry, I encountered an error. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, messages, buildContext, router, startTour, sendMessageInternal, onClose, openWidget, maximizeWidget]);

  // Welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: "welcome",
        role: "assistant",
        content: `Hi${user?.user?.name ? ` ${user.user.name.split(" ")[0]}` : ""}! I'm ${branding.botName || "InsuraBot"} AI Support. I can help you navigate the app, explain features, or answer questions. What can I help you with?`,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length, user?.user?.name]);

  if (!isOpen) return null;

  // Position classes based on dialer position
  const positionClasses = position === 'left'
    ? 'right-full mr-2' // Panel appears to the left of dialer
    : 'left-full ml-2'; // Panel appears to the right of dialer

  return (
    <div className={embedded
      ? `relative w-full h-full bg-white flex flex-col overflow-hidden`
      : `absolute bottom-0 ${positionClasses} w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden z-50`}>
      {/* Header — hidden when embedded (the host shell, e.g. the InsuraBot
          "Helper" tab, supplies its own header + tabs). */}
      {!embedded && (
      <div className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground">
        <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
          <InsuraBotIcon size={28} altText={branding.brandName || "InsuraBot"} logoSrc={branding.logoUrl || branding.faviconUrl} />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <h3 className="font-semibold text-sm">{branding.botName || "InsuraBot"} AI Support</h3>
          <span className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded-full font-medium">Beta</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* AI Assist card — let InsuraBot take control & guide you (co-browse
            AI mode). Ported from the retired floating "?" widget so the Helper
            tab keeps the "AI Assist" button. Hidden once AI assist is active. */}
        {!isMobileShell && !coBrowse?.aiAssistActive && !showCoBrowsePanel && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <MousePointer2 className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">AI Assist</p>
                <p className="text-[11px] text-gray-500 leading-tight">Let InsuraBot take control &amp; guide you</p>
              </div>
              <button
                onClick={async () => {
                  setIsStartingCoBrowse(true);
                  setCoBrowseError(null);
                  try {
                    await coBrowse.requestAiAssist();
                    setMessages([{
                      id: `ai-assist-${Date.now()}`,
                      role: "assistant",
                      content: "InsuraBot AI is now analyzing your screen. I'll guide you through whatever you need help with!",
                      timestamp: new Date(),
                    }]);
                    setCollapseQuickActions(true);
                  } catch (err) {
                    setCoBrowseError(err instanceof Error ? err.message : 'Failed to start AI assist');
                  } finally {
                    setIsStartingCoBrowse(false);
                  }
                }}
                disabled={isStartingCoBrowse}
                className="flex-shrink-0 px-3.5 py-2 text-xs font-semibold bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors duration-200 flex items-center gap-1.5"
              >
                {isStartingCoBrowse ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                {isStartingCoBrowse ? 'Starting...' : 'Start'}
              </button>
            </div>
            {coBrowseError && (
              <div className="mt-2 flex items-center gap-1.5 text-red-600 text-[11px]">
                <AlertCircle className="w-3 h-3" />
                {coBrowseError}
              </div>
            )}
          </motion.div>
        )}

        {messages.map((message) => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                message.role === "user"
                  ? "bg-primary text-primary-foreground rounded-br-md"
                  : "bg-gray-100 text-gray-800 rounded-bl-md"
              }`}
            >
              {message.role === "user" ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {message.content}
                </p>
              ) : (
                <div className="flex items-start gap-2 min-w-0">
                  <BotGlyph />
                  <p className="flex-1 min-w-0 text-sm leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {/* Quick Actions - Always visible, collapsible after first message.
            Not on the shell: they launch desktop tours/navigation. */}
        {!isMobileShell && !isLoading && !showCoBrowsePanel && (
          <QuickActionsPanel
            quickActions={quickActions}
            categoryColors={categoryColors}
            onAction={handleQuickAction}
            onStartTour={async (tourId) => {
              // Close the panel first to not interfere with tour
              onClose();
              // Reset and start the specific tour after panel animation completes
              setTimeout(async () => {
                await resetTour(tourId);
                // Small delay between reset and start to ensure state is updated
                setTimeout(() => {
                  startTour(tourId);
                }, 50);
              }, 200);
            }}
            onResetAllTours={async () => {
              // Close the panel first to not interfere with tour
              onClose();
              // Reset all tours and start from the beginning after panel animation completes
              setTimeout(async () => {
                await resetAllTours();
                // Small delay between reset and start to ensure state is updated
                setTimeout(() => {
                  startTour('sidebar-navigation-tour');
                }, 50);
              }, 200);
            }}
            isInitialState={messages.length === 1 && messages[0].id === "welcome"}
            availableTours={availableTours}
            tourProgress={progress}
            currentPath={pathname}
            isAgencyOwner={user?.currentAgency?.role === 'owner'}
            isAdmin={user?.user?.isSystemAdmin || false}
            enabledFeatures={{ quoteAndEnroll: quoteEnrollEnabled }}
            forceCollapse={collapseQuickActions}
          />
        )}

        {/* Streaming message - real-time typing effect */}
        {isLoading && streamingContent && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="max-w-[85%] px-4 py-2.5 rounded-2xl bg-gray-100 text-gray-800 rounded-bl-md">
              <div className="flex items-start gap-2 min-w-0">
                <BotGlyph pulse />
                <p className="flex-1 min-w-0 text-sm leading-relaxed whitespace-pre-wrap">
                  {streamingContent}
                  <span className="inline-block w-1.5 h-4 ml-0.5 bg-primary animate-pulse rounded-sm" />
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Loading indicator - only show when waiting for first chunk */}
        {isLoading && !streamingContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
              <div className="flex items-center gap-2">
                <BotGlyph pulse />
                <span className="text-sm text-gray-500">Thinking...</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Co-Browse Panel */}
        <AnimatePresence>
          {showCoBrowsePanel && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3"
            >
              {/* Not started state */}
              {!coBrowse?.session && (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Eye className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">Request Live Assistance</h4>
                      <p className="text-xs text-gray-500">A support agent will view and guide your screen</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 flex items-start gap-2">
                    <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-gray-600">
                      <p className="font-medium mb-1">Privacy Protected</p>
                      <ul className="space-y-0.5 text-gray-500">
                        <li>• Passwords are automatically hidden</li>
                        <li>• You control when remote control is allowed</li>
                        <li>• You can end the session anytime</li>
                      </ul>
                    </div>
                  </div>

                  {coBrowseError && (
                    <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 p-2 rounded">
                      <AlertCircle className="w-4 h-4" />
                      {coBrowseError}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCoBrowsePanel(false)}
                      className="flex-1 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleStartCoBrowse}
                      disabled={isStartingCoBrowse}
                      className="flex-1 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-primary/40 flex items-center justify-center gap-2"
                    >
                      {isStartingCoBrowse ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <Users className="w-4 h-4" />
                          Start Session
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Waiting state */}
              {coBrowse?.session?.status === 'waiting' && (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">Waiting for Support</h4>
                      <p className="text-xs text-gray-500">A support agent will join shortly...</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    Screen sharing active
                  </div>

                  <button
                    onClick={handleEndCoBrowse}
                    className="w-full py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                  >
                    <PhoneOff className="w-4 h-4" />
                    Cancel Request
                  </button>
                </>
              )}

              {/* Active state */}
              {coBrowse?.session?.status === 'active' && (
                <>
                  <div className="flex items-center gap-3 bg-green-50 p-3 rounded-lg">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {coBrowse.session.adminName?.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{coBrowse.session.adminName || 'Support Agent'}</p>
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Connected and viewing your screen
                      </p>
                    </div>
                    <button
                      onClick={() => setShowLiveChat(!showLiveChat)}
                      className={`p-2 rounded-lg transition-colors ${showLiveChat ? 'bg-primary text-primary-foreground' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Live Chat Panel */}
                  <AnimatePresence>
                    {showLiveChat && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        {/* Chat Messages */}
                        <div className="h-32 overflow-y-auto p-2 space-y-2 bg-gray-50">
                          {coBrowse.chatMessages?.length === 0 && (
                            <p className="text-xs text-gray-400 text-center py-4">
                              Chat with your support agent
                            </p>
                          )}
                          {coBrowse.chatMessages?.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.senderType === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[80%] px-2.5 py-1.5 rounded-lg text-xs ${
                                  msg.senderType === 'user'
                                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                                }`}
                              >
                                {msg.content}
                              </div>
                            </div>
                          ))}
                          {coBrowse.isAdminTyping && (
                            <div className="flex justify-start">
                              <div className="bg-white text-gray-500 px-2.5 py-1.5 rounded-lg text-xs border border-gray-200 rounded-bl-sm">
                                <span className="flex items-center gap-1">
                                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </span>
                              </div>
                            </div>
                          )}
                          <div ref={liveChatEndRef} />
                        </div>

                        {/* Chat Input */}
                        <div className="p-2 bg-white border-t border-gray-200 flex gap-2">
                          <input
                            type="text"
                            value={liveChatInput}
                            onChange={(e) => handleLiveChatTyping(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSendLiveChat()}
                            placeholder="Message support..."
                            className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                          <button
                            onClick={handleSendLiveChat}
                            disabled={!liveChatInput.trim()}
                            className="p-1.5 bg-primary text-primary-foreground rounded-lg disabled:opacity-50 hover:bg-primary/90"
                          >
                            <Send className="w-3 h-3" />
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MousePointer2 className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">Remote Control</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      coBrowse.session.remoteControlEnabled
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {coBrowse.session.remoteControlEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>

                  <button
                    onClick={handleEndCoBrowse}
                    className="w-full py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                  >
                    <PhoneOff className="w-4 h-4" />
                    End Session
                  </button>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Control Request Modal */}
        <AnimatePresence>
          {coBrowse?.controlRequest && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border-2 border-primary/20 rounded-xl p-4 shadow-lg space-y-3"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <MousePointer2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">Remote Control Request</h4>
                  <p className="text-xs text-gray-500">
                    {coBrowse.session?.adminName || 'Support'} wants to control your screen
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg p-3 flex items-start gap-2">
                <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  They will be able to click buttons, scroll, and navigate on your behalf.
                  You can revoke access at any time.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => coBrowse.respondToControlRequest(false)}
                  className="flex-1 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Deny
                </button>
                <button
                  onClick={() => coBrowse.respondToControlRequest(true)}
                  className="flex-1 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Allow
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={messagesEndRef} />
      </div>

      {/* Input — matches the InsuraBot "Client" tab composer exactly: soft
          card, primary border on focus (no halo), solid primary-gradient send
          square, Textarea with Shift+Enter for a new line. */}
      <div className="p-3 border-t border-gray-100">
        <div className="flex space-x-2 items-end bg-white dark:bg-gray-900 rounded-lg p-1.5 border border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-150 focus-within:border-primary">
          <Textarea
            ref={inputRef as any}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={`Ask me anything about using ${branding.brandName}... (Shift+Enter for new line)`}
            disabled={isLoading}
            rows={3}
            className="flex-1 resize-none !border-0 bg-transparent text-[15px] placeholder:text-gray-400 placeholder:text-[14px] focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-2 min-h-[72px] max-h-[160px]"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            aria-label="Send message"
            className={
              "h-10 w-10 shrink-0 rounded-md flex items-center justify-center transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 " +
              (!inputValue.trim() || isLoading
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-sm shadow-primary/20 hover:from-primary hover:to-primary active:opacity-90")
            }
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
        {!branding.hidePoweredBy && (
          <p className="text-xs text-gray-400 mt-2 text-center">
            Powered by {branding.brandName}
          </p>
        )}
      </div>
    </div>
  );
}

export default InsuraHelpPanel;
